import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Truck, Thermometer, Clock, Building2, HardHat, Briefcase, CheckCircle2 } from 'lucide-react';
import { BRAND, PROCESS } from '../data/mock';
import { Reveal, SplitText, Magnetic, AnimatedDivider, useParallax } from '../components/effects';
import CTASection from '../components/CTASection';

const HERO_IMG =
  '/site/legacy-yemek-02.webp';
const SEGMENT_IMG =
  '/site/tasima-yemek-hero.webp';

const SEGMENTS = [
  {
    Icon: Building2,
    title: 'Fabrikalar',
    desc: 'Üretim hattınızın temposunu yakalayan vardiya yemeği operasyonu. 50–5.000 kişi.',
    scale: '50–5.000 kişi',
  },
  {
    Icon: Briefcase,
    title: 'Ofisler & Plazalar',
    desc: 'Diyetisyen onaylı, hafif ve dengeli öğle menüleri. Kurumsal ofislere uygun servis.',
    scale: '30–500 kişi',
  },
  {
    Icon: HardHat,
    title: 'Şantiyeler',
    desc: 'Sahaya zamanında, sıcak ve hijyenik teslimat. Lojistik zorluklarda uzmanız.',
    scale: 'Esnek kapasite',
  },
];

const OPS = [
  {
    Icon: Truck,
    title: 'GPS Takipli Filo',
    desc: '22 araçtan oluşan izotermli soğuk-sıcak zincir filomuz, her teslimatı dakikası dakikasına izler.',
  },
  {
    Icon: Thermometer,
    title: 'Isı Garantisi',
    desc: 'Sıcak yemek 65°C üstü, soğuk yemek 4°C altı. Termal kayıt cihazlarıyla belgelenir.',
  },
  {
    Icon: Clock,
    title: 'Vardiya Senkronu',
    desc: 'Çift vardiya, gece vardiyası, tatil günleri — operasyonunuza göre planlama.',
  },
];

const INCLUDED = [
  'Beslenme uzmanı onaylı 28 günlük menü',
  'Diyabet, çölyak, vejetaryen seçenekleri',
  '4 çeşit standart öğün (çorba + ana + yan + tatlı)',
  'İzotermli kap & kapaklı tabak',
  'Üniformalı saha personeli',
  'Servis sonrası temizlik & toplama',
  '14 noktalı günlük kalite kontrol raporu',
  'Aylık çalışan memnuniyet anketi',
  'Termal kayıtlı sıcaklık takibi',
  'GPS izlenebilir teslim',
  'Atanmış müşteri yöneticisi',
  '7/24 operasyon hattı',
];

/* HERO */
const HeroBlock = () => {
  return (
    <section className="relative h-[78vh] min-h-[620px] w-full overflow-hidden bg-[#F5F3EF] pt-[170px] lg:pt-[200px]">
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover animate-[heroZoom_9s_ease-out_forwards]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(245,243,239,0.62) 0%, rgba(245,243,239,0.12) 16%, rgba(245,243,239,0) 38%, rgba(245,243,239,0.5) 76%, rgba(245,243,239,0.95) 100%), linear-gradient(90deg, rgba(245,243,239,0.35) 0%, rgba(245,243,239,0) 48%)',
          }}
        />
      </div>

      <div className="relative z-10 h-full flex items-end pb-20 lg:pb-28">
        <div className="container-x w-full">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-7">
              <AnimatedDivider width="3rem" />
              <Reveal delay={100} y={8} duration={700}>
                <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                  Ana Hizmet
                </span>
              </Reveal>
            </div>

            <h1
              className="font-display text-[44px] sm:text-[64px] lg:text-[92px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A]"
              style={{ fontWeight: 500 }}
            >
              <SplitText text="Taşıma yemek" delay={120} />
              <br />
              <span className="italic font-light text-[#9A7F4F]">
                <SplitText text="hizmeti." delay={520} />
              </span>
            </h1>

            <Reveal delay={900}>
              <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/72 max-w-2xl leading-[1.7]">
                Merkezi mutfağımızda günlük taze üretilen yemekler; izotermli araçlarla,
                vardiyanıza uygun saatte, yemekhanenize teslim edilir. Üretimden servise
                tek operasyonel zincir.
              </p>
            </Reveal>

            <Reveal delay={1100}>
              <div className="mt-12 flex flex-wrap items-center gap-7">
                <Magnetic strength={0.2}>
                  <Link
                    to="/iletisim"
                    className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F3EF] px-8 py-[18px] text-[12px] tracking-[0.22em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#9A7F4F]"
                    data-testid="tasima-hero-cta"
                  >
                    Teklif Al
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </Magnetic>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
                >
                  <Phone size={13} className="text-[#9A7F4F]" /> {BRAND.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* SEGMENTS */
const SegmentsBlock = () => {
  return (
    <section className="relative py-28 lg:py-44 bg-[#F5F3EF] overflow-hidden">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 lg:mb-24 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-7">
              <AnimatedDivider width="3rem" />
              <Reveal delay={100} y={8} duration={700}>
                <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                  Kim İçin?
                </span>
              </Reveal>
            </div>
            <Reveal delay={150}>
              <h2
                className="font-display text-[36px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
                style={{ fontWeight: 500 }}
              >
                Üç farklı saha.{' '}
                <span className="italic font-light text-[#9A7F4F]">Tek standart</span>.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <Reveal delay={350}>
              <p className="text-[15px] lg:text-[16px] text-[#1A1A1A]/68 leading-[1.7]">
                50 kişilik ofisten 5.000 kişilik üretim tesisine kadar, operasyonel disiplin
                değişmez. Hizmet kalitemiz, müşteri ölçeğinden bağımsızdır.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#9A7F4F]/22">
          {SEGMENTS.map((s, i) => (
            <Reveal key={s.title} delay={i * 160}>
              <article className="bg-[#F5F3EF] p-9 lg:p-12 h-full">
                <div className="flex items-center gap-3 mb-7">
                  <span className="text-[10.5px] numeric tracking-[0.32em] text-[#9A7F4F] font-medium">
                    0{i + 1}
                  </span>
                  <span className="h-px w-10 bg-[#9A7F4F]/55" />
                </div>
                <s.Icon size={28} strokeWidth={1.4} className="text-[#9A7F4F] mb-6" />
                <h3
                  className="font-display text-[28px] lg:text-[34px] text-[#1A1A1A] leading-[1.1] tracking-[-0.01em]"
                  style={{ fontWeight: 500 }}
                >
                  {s.title}
                </h3>
                <p className="mt-4 text-[14.5px] text-[#1A1A1A]/68 leading-[1.7]">{s.desc}</p>
                <div className="mt-7 inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#9A7F4F] font-medium border-b border-[#9A7F4F]/30 pb-1.5">
                  Ölçek · {s.scale}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* OPS */
const OpsBlock = () => {
  const imgRef = useParallax(0.07);
  return (
    <section className="relative py-28 lg:py-44 bg-[#72272D] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.20]">
        <img
          ref={imgRef}
          src={SEGMENT_IMG}
          alt=""
          className="w-full h-full object-cover will-change-transform"
          style={{ transform: 'scale(1.12)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#72272D]/70 via-[#72272D]/55 to-[#72272D]" />
      </div>
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A875]/45 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A875]/45 to-transparent" />

      <div className="container-x relative">
        <div className="max-w-3xl mb-14 lg:mb-20">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" color="#C9A875" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#C9A875] font-medium">
                Operasyonel Güç
              </span>
            </Reveal>
          </div>
          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] lg:text-[58px] text-[#F5F0E5] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              Lojistik bizim için yemek kadar{' '}
              <span className="italic font-light text-[#C9A875]">önemlidir</span>.
            </h2>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-x-10 gap-y-14">
          {OPS.map((o, i) => (
            <Reveal key={o.title} delay={i * 160} y={28}>
              <div className="border-t border-[#C9A875]/35 pt-7">
                <o.Icon size={28} strokeWidth={1.4} className="text-[#C9A875] mb-5" />
                <h3
                  className="font-display text-[24px] lg:text-[28px] text-[#F5F0E5] leading-[1.15] tracking-[-0.005em]"
                  style={{ fontWeight: 500 }}
                >
                  {o.title}
                </h3>
                <p className="mt-4 text-cream-78 text-[14px] leading-[1.7]">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* PROCESS */
const ProcessBlock = () => {
  return (
    <section className="relative py-28 lg:py-44 bg-[#F5F3EF] overflow-hidden">
      <div className="container-x">
        <div className="max-w-3xl mb-14 lg:mb-20">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                Süreç
              </span>
            </Reveal>
          </div>
          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              Altı adımda{' '}
              <span className="italic font-light text-[#9A7F4F]">kurumsal disiplin</span>.
            </h2>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 130} y={28}>
              <div className="border-t border-[#9A7F4F]/35 pt-6">
                <div className="flex items-baseline gap-5 mb-5">
                  <span className="font-display numeric text-[#9A7F4F] text-[34px] lg:text-[42px] leading-none font-medium">
                    {p.step}
                  </span>
                  <h3
                    className="font-display text-[22px] lg:text-[26px] text-[#1A1A1A] leading-[1.2] tracking-[-0.005em]"
                    style={{ fontWeight: 500 }}
                  >
                    {p.title}
                  </h3>
                </div>
                <p className="text-[14px] text-[#1A1A1A]/65 leading-[1.7]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* INCLUDED */
const IncludedBlock = () => {
  return (
    <section className="relative py-28 lg:py-44 bg-[#EFEBE3] overflow-hidden">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                Sözleşmeye Dahil
              </span>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] lg:text-[54px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              Açık ücret,{' '}
              <span className="italic font-light text-[#9A7F4F]">gizli madde yok</span>.
            </h2>
          </Reveal>

          <Reveal delay={350}>
            <p className="mt-7 text-[15.5px] text-[#1A1A1A]/72 leading-[1.7] max-w-md">
              Her sözleşme tek sayfalık, sade bir kapsam ve sabit fiyat çizelgesiyle başlar.
              Operasyon sırasında ek kalem talep etmiyoruz.
            </p>
          </Reveal>

          <Reveal delay={500}>
            <Magnetic strength={0.18}>
              <Link
                to="/iletisim"
                className="group mt-9 inline-flex items-center gap-2 text-[#1A1A1A] text-[12.5px] tracking-[0.18em] uppercase font-medium border-b border-[#1A1A1A]/30 pb-1.5 hover:border-[#9A7F4F] hover:text-[#9A7F4F] transition-colors"
                data-testid="contract-sample-cta"
              >
                Sözleşme Örneği İste
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
          </Reveal>
        </div>

        <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 self-center">
          {INCLUDED.map((t, i) => (
            <Reveal key={t} delay={300 + i * 50} y={14}>
              <li className="flex items-start gap-3 py-3 border-b border-[#1A1A1A]/[0.10]">
                <CheckCircle2 size={16} strokeWidth={1.5} className="text-[#9A7F4F] shrink-0 mt-1" />
                <span className="text-[14px] text-[#1A1A1A]/85 leading-[1.55]">{t}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

const TasimaYemek = () => {
  return (
    <>
      <HeroBlock />
      <SegmentsBlock />
      <OpsBlock />
      <ProcessBlock />
      <IncludedBlock />
      <CTASection />
    </>
  );
};

export default TasimaYemek;
